import { format } from "date-fns"
import { sum } from "./number"
import { round } from "@cd2/tools"

export function randomString(
  length: number,
  opts: { upper?: boolean; lower?: boolean; numbers?: boolean } = {
    upper: true,
    lower: true,
    numbers: true,
  },
) {
  const lower = "abcdefghijklmnopqrstuvwxyz"
  const numbers = "0123456789"

  let alphabet = ""
  if (!opts || opts.lower) {
    alphabet += lower
  }
  if (opts.upper) {
    alphabet += lower.toUpperCase()
  }
  if (opts.numbers) {
    alphabet += numbers
  }

  if (alphabet === "") {
    throw new Error("Cannot generate a random string without an alphabet")
  }

  return Array(length)
    .fill(null)
    .map(() => alphabet.charAt(Math.floor(Math.random() * alphabet.length)))
    .join("")
}

export function formatDateTilDate(date1?: Date | string | null, date2?: Date | string | null) {
  if (!date1) {
    return ""
  }
  const d1 = new Date(date1)
  const d2 = date2 ? new Date(date2) : undefined
  return [format(d1, "do MMM yyyy"), d2 ? format(d2, "do MMM yyyy") : undefined]
    .filter(Boolean)
    .join(" until ")
}

export function initials(firstName: string, lastName: string) {
  return [firstName, lastName]
    .filter(Boolean)
    .map(e => e[0])
    .join("")
}

export function isValidEmail(email: string) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(String(email).toLowerCase())
}

// This function takes a count and a format string and returns a string
// that is formatted based on the count.
//
// The format string is a string that contains the following special
// characters:
//
// %s - if count is 1, replace with "", otherwise replace with "s"
// %c - replace with the count
// %[singular,plural] - if count is 1, replace with singular, otherwise
// replace with plural
//
// For example, the format string "%c%s" will return "1" if count is 1,
// and "2s" otherwise. The format string "%c %[apple,apples]" will return
// "1 apple" if count is 1, and "2 apples" otherwise.
export function pluralize(count: number, formatString: string) {
  const value = formatString
    .replace(/%s/g, count === 1 ? "" : "s")
    .replace(/%c/g, count.toString())
    .replace(/%\[(.*?)\]/g, (_, match) => {
      const [singular, plural] = match.split(",")
      return count === 1 ? singular : plural
    })

  if (process.env.NODE_ENV === "development") {
    const matches = value.match(/%(\w)/g)
    if (matches) {
      console.warn(
        `Unmatched pluralize format string: ${formatString}. Matches: ${matches.join(", ")}`,
      )
    }
  }

  return value
}

export function fuzzyMatch(vector: string[], query: string) {
  const queryParts = query.toLowerCase().split(" ").filter(Boolean)
  if (queryParts.length === 0) return true

  const vectorParts = vector
    .flatMap(part => part.split(" "))
    .map(part => part.toLowerCase())
    .filter(Boolean)

  return queryParts.every(queryPart => vectorParts.some(part => part.startsWith(queryPart)))
}

export function fuzzySearchScore(opts: {
  vector: string[]
  query: string
  matchAll?: boolean
  prioritizeStart?: boolean
}) {
  const queryParts = opts.query.toLowerCase().split(" ").filter(Boolean)
  if (queryParts.length === 0) {
    return 1
  }

  const vectorParts = opts.vector.flatMap(part => part.toLowerCase().split(" ")).filter(Boolean)

  const partScores = queryParts.map(queryPart => {
    const vectorMatches = vectorParts
      .map(v => v.indexOf(queryPart))
      .filter(v => v !== -1)
      .map(index => 1 / (index + 1))

    if (vectorMatches.length === 0) {
      return -1
    } else {
      return sum(vectorMatches)
    }
  })

  if (opts.matchAll && partScores.some(score => score === -1)) {
    return -1
  } else if (partScores.every(score => score === -1)) {
    return -1
  } else {
    let score = sum(partScores.filter(score => score > 0))

    if (opts.prioritizeStart) {
      opts.vector.forEach(part => {
        if (part.toLowerCase().startsWith(queryParts.join(" "))) {
          score *= 10
        }
      })
    }

    return score
  }
}

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export function titleize(str: string) {
  return str.split(" ").map(capitalize).join(" ")
}

export function truncate(str: string, opt: { length: number; omission?: string }) {
  const omission = opt.omission ?? "..."
  if (str.length <= opt.length) {
    return str
  }
  const show = str.slice(0, opt.length - omission.length)
  return show + omission
}

// Simple tag stripping for display/export use-cases (not a full HTML sanitizer).
export function stripTags(original: string) {
  return original.replace(/(<([^>]+)>)/gi, "")
}

export function upperCase<T extends string>(str: T): `${Uppercase<T>}` {
  return str.toUpperCase() as `${Uppercase<T>}`
}

export function randomLetter() {
  const alphabet = "abcdefghijklmnopqrstuvwxyz"
  return alphabet[Math.floor(Math.random() * alphabet.length)].toUpperCase()
}

export function formatPennies(amount: number, opts?: { withPence?: boolean }) {
  const pennies = amount % 100
  const pounds = Math.floor(amount / 100)

  if (!opts?.withPence && pennies === 0) {
    return `£${pounds}`
  } else {
    return `£${pounds}.${pennies.toString().padStart(2, "0")}`
  }
}

/* 0.2 => 20% */
export function formatPercentage(value: number, dp = 0) {
  return `${round(value * 100, dp ?? 0)}%`
}

export function formatFileSize(fileSize: number): string {
  if (fileSize < 1024) {
    return `${fileSize} bytes`
  } else if (fileSize < 1024 * 1024) {
    return `${(fileSize / 1024).toFixed(1)} KB`
  } else if (fileSize < 1024 * 1024 * 1024) {
    return `${(fileSize / 1024 / 1024).toFixed(1)} MB`
  } else {
    return `${(fileSize / 1024 / 1024 / 1024).toFixed(1)} GB`
  }
}
